/**
 * Web Search Integration (Client-side handler)
 * Sends search requests to the Worker and processes results.
 */

import { API_BASE } from './api.js';

/**
 * Keywords that indicate the user wants current/real-time information
 */
const SEARCH_TRIGGER_PATTERNS = [
  /\b(latest|current|recent|today|yesterday|this week|this month|this year|2025|2026|right now|breaking)\b/i,
  /\b(news|update|announcement|release|launch|price|stock|weather|score|result)\b/i,
  /\b(who won|what happened|is .+ open|when does|when is|where can i)\b/i,
  /\b(search for|look up|find out|google)\b/i,
];

/**
 * Detect if a query would benefit from web search
 * @param {string} query
 * @returns {boolean}
 */
export function shouldSearch(query) {
  if (!query || query.length < 5) return false;
  return SEARCH_TRIGGER_PATTERNS.some(pattern => pattern.test(query));
}

/**
 * Perform a web search through the Worker API
 * @param {string} query - Search query
 * @returns {Promise<Array<{title: string, url: string, snippet: string}>>}
 */
export async function performSearch(query) {
  try {
    const headers = { 'Content-Type': 'application/json' };
    try {
      const session = JSON.parse(localStorage.getItem('chorus_session') || '{}');
      if (session.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }
    } catch {}

    const res = await fetch(`${API_BASE}/api/search`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ query }),
    });

    if (!res.ok) {
      console.warn('Search API error:', res.status);
      return [];
    }

    const data = await res.json();
    return data.results || [];
  } catch (error) {
    console.warn('Search failed:', error);
    return [];
  }
}

/**
 * Format search results as context for AI prompt injection
 * @param {Array<{title: string, url: string, snippet: string}>} results
 * @returns {string}
 */
export function formatSearchContext(results) {
  if (!results || results.length === 0) return '';

  const formatted = results.slice(0, 5).map((r, i) =>
    `[${i + 1}] "${r.title}"\n${r.snippet}\nSource: ${r.url}`
  ).join('\n\n');

  return `[Web Search Results]\nThe following are recent web search results relevant to the user's query:\n\n${formatted}\n\n[End Search Results]\n\nUse these search results to provide accurate, up-to-date information. Cite sources where appropriate.`;
}
